// hooks
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';

// styles
import dayjs from 'dayjs';
import {
  LogEntryContainer,
  LogEntryLabel,
  MetaContent,
  LeftContent,
  Metadata,
  InfoSection,
  InfoRow,
  IconWrapper,
  InfoLabel
  // StyledIconButton,
  // ChangeLogContent,
  // ChangeLogList,
  // ChangeLogListItem,
  // ChangeLogChip,
  // ChangeLogContentDivider
} from './LogEntry.styles';
import { Icon, IconButton } from '@sdb/planning-ui';
import { getIconType, getIconColor } from 'components/mrp/mrp.utils';

// types
import { LogEntryProps } from './LogEntryProps.types';

// utils
import {
  getChangedEventPreviewLabel,
  getLocationInfo,
  getEmployeeInfo,
  getClientInfo,
  getUndoCommandTitle
} from './LogEntry.utils';
import { Box, Tooltip } from '@mui/material';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import { omit } from 'lodash';

// API
import { requests, queryKeys } from 'RestAPIs/rapini';
import { useEntityStore } from 'components/mrp/stores/entityStore/entityStore';
import { useShallow } from 'zustand/react/shallow';

export const LogEntry = ({ command, isFirst, undoCommand }: LogEntryProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { changeEvents, createdAt, commandId } = command;

  // Get the actual calendar's date range
  const { startDate: calendarStartDate, endDate: calendarEndDate } =
    useSelectedDateRange();

  const { refetchPlanning, subscribers } = useEntityStore(
    useShallow((state) => ({
      refetchPlanning: state.control.fetch,
      subscribers: state.subscribers
    }))
  );

  const changeEvent = changeEvents?.[changeEvents.length - 1];

  const metadataLabel = getChangedEventPreviewLabel(changeEvent);
  const locationInfo = getLocationInfo(changeEvent);
  const employeeInfo = getEmployeeInfo(changeEvent);
  const clientInfo = getClientInfo(changeEvent);

  const isUndone = !!undoCommand;

  const handleUndo = async () => {
    if (!commandId) {
      console.error('Cannot undo: commandId is missing');
      return;
    }

    try {
      await requests.undoChanges({ commandId });

      showSuccessSnackbar();

      // Refresh audit log
      await queryClient.invalidateQueries({
        queryKey: queryKeys.findAuditedCommands(
          dayjs().startOf('day').toISOString(),
          dayjs().endOf('day').toISOString()
        )
      });

      // Refresh calendar
      if (subscribers.length > 0) {
        await refetchPlanning({
          planningEntityIdentifiers: subscribers.map((subscriber) =>
            omit(subscriber, 'isError', 'isLoading')
          ),
          startDate: calendarStartDate,
          endDate: calendarEndDate
        });
      }
    } catch (error) {
      console.error('Failed to undo command:', error);
      showErrorSnackbar();
    }
  };

  return (
    <LogEntryContainer isUndone={isUndone}>
      <Box>
        <MetaContent>
          <LeftContent>
            {metadataLabel && <Metadata>{metadataLabel}</Metadata>}
          </LeftContent>
          {isFirst && commandId && !isUndone && (
            <Tooltip
              title={t('mrp.contextPanel.commandType.Undo')}
              placement="left"
            >
              <IconButton
                onClick={handleUndo}
                size="small"
                type="arrow-rotate-left"
                sx={{
                  bgcolor: 'rgba(0, 177, 170, 0.1)'
                }}
              />
            </Tooltip>
          )}
        </MetaContent>

        <InfoSection>
          {locationInfo && (
            <InfoRow>
              <IconWrapper iconColor={getIconColor('WORKPLACE')}>
                <Icon type="location-dot" />
              </IconWrapper>
              <InfoLabel>{locationInfo}</InfoLabel>
            </InfoRow>
          )}

          {employeeInfo.length > 0 && (
            <InfoRow>
              <IconWrapper iconColor={getIconColor('EMPLOYEE')}>
                <Icon type={getIconType('EMPLOYEE')} />
              </IconWrapper>
              <InfoLabel>{employeeInfo.join(', ')}</InfoLabel>
            </InfoRow>
          )}

          {clientInfo.length > 0 && (
            <InfoRow>
              <IconWrapper iconColor={getIconColor('CLIENT')}>
                <Icon type={getIconType('CLIENT')} />
              </IconWrapper>
              <InfoLabel>{clientInfo.join(', ')}</InfoLabel>
            </InfoRow>
          )}
        </InfoSection>
        <Box display="flex" flexDirection="column" gap={1}>
          <LogEntryLabel
            variant="filled"
            label={getUndoCommandTitle(command.type, createdAt)}
            size="small"
            type={command.type}
            isUndone={isUndone}
          />
          {undoCommand && (
            <LogEntryLabel
              variant="filled"
              label={getUndoCommandTitle(
                undoCommand.type,
                undoCommand.createdAt,
                command.type
              )}
              size="small"
              type={undoCommand.type}
              isUndone={false}
            />
          )}
        </Box>
      </Box>
    </LogEntryContainer>
  );
};
